/**
 * Código Alt — cómo un lector en modo teclado transmite caracteres que no
 * existen en el teclado, en particular el separador GS (ASCII 29) de GS1:
 * mantiene Alt y teclea el código en el teclado numérico (`Alt+0029`), a veces
 * rodeado de NumLock para asegurarse de que el numérico produzca dígitos.
 *
 * Esta máquina de estados decide, por cada keydown, si la tecla es un dígito
 * del código (se absorbe: no debe insertarse como texto), si es un modificador
 * (se ignora) o si es cualquier otra tecla (cierra el código en curso y dice
 * qué carácter insertar antes de procesarla).
 *
 * Regla que salió de trazas reales (Zebra DS2208, 2026-09-16): el lector a
 * veces suelta y vuelve a apretar Alt en medio del código
 * (`Alt↓ 0 0 2 Alt↑ Alt↓ 9 Alt↑`). Cerrar el código en el keyup de Alt daba
 * `002` y `9`, ninguno es 29, y el separador se perdía en silencio. Por eso el
 * cierre lo dispara la primera tecla que no sea un modificador ni un dígito
 * del numérico (NumLock, la siguiente tecla del texto o Enter), y un dígito del
 * numérico que llegue sin Alt con un código abierto también se absorbe.
 *
 * Pura, sin React: se testea con las trazas tal como llegaron.
 */

import { GS1_SEPARADOR_VISIBLE } from '@/domain/gs1';

export interface TeclaCodigoAlt {
  key: string;
  code: string;
  altKey: boolean;
}

export type DecisionCodigoAlt =
  /** Dígito del código: hacer preventDefault y no insertar nada. */
  | { tipo: 'absorbida' }
  /** Un modificador (Alt, Shift, …): no hacer nada. */
  | { tipo: 'modificador' }
  /** Cualquier otra tecla: el código quedó cerrado; `insertar` va antes de la tecla (puede ser ''). */
  | { tipo: 'cierra'; insertar: string };

/** Largo máximo de un código Alt de Windows (`0` + hasta cuatro dígitos). */
export const MAX_DIGITOS_CODIGO_ALT = 5;

/**
 * Sin Alt, un dígito del numérico solo se toma como parte del código si hay
 * uno abierto y todavía corto: cubre el rebote del modificador sin comerse
 * dígitos de texto que un lector mandara por el teclado numérico.
 */
const MAX_DIGITOS_SIN_ALT = 4;

const MODIFICADORES = new Set(['Alt', 'AltGraph', 'Shift', 'Control', 'Meta']);

const ES_DIGITO_NUMPAD = /^Numpad\d$/;

/**
 * Carácter que representa un código Alt cerrado. 29 es el GS y se inserta en
 * su forma visible; los códigos de ASCII imprimible se insertan tal cual (para
 * lectores con emulación completa del teclado numérico); el resto se descarta.
 */
export function caracterDeCodigoAlt(digitos: string): string {
  if (!digitos) return '';
  const n = parseInt(digitos, 10);
  if (n === 29) return GS1_SEPARADOR_VISIBLE;
  if (n >= 32 && n <= 126) return String.fromCharCode(n);
  return '';
}

export function pasoCodigoAlt(
  buffer: string,
  tecla: TeclaCodigoAlt,
): { buffer: string; decision: DecisionCodigoAlt } {
  if (ES_DIGITO_NUMPAD.test(tecla.code)) {
    const enCurso = buffer.length > 0 && buffer.length < MAX_DIGITOS_SIN_ALT;
    if (tecla.altKey || enCurso) {
      const nuevo = buffer.length < MAX_DIGITOS_CODIGO_ALT ? buffer + tecla.code.slice(-1) : buffer;
      return { buffer: nuevo, decision: { tipo: 'absorbida' } };
    }
  }
  if (MODIFICADORES.has(tecla.key)) return { buffer, decision: { tipo: 'modificador' } };
  return { buffer: '', decision: { tipo: 'cierra', insertar: caracterDeCodigoAlt(buffer) } };
}
